import React, { useEffect, useState } from 'react';
import {
  Box,
  MuiBox,
  Drawer,
  Typography,
  Button,
  SvgIconButton,
  DotProgress,
  Card,
  Backdrop,
  FlexBox,
  InputField,
  Grid,
  color,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import {
  actions as kioskHardwareActions,
  selectors as kioskHardwareSelectors,
  KioskHardware
} from 'store/kioskHardware';
import { useStyles } from '../../styled';
import { getKioskHardwareInitialForm } from '../../utils';
import { MappingEditDrawerProps } from '../../types';
import { INVALID, VALID } from '../../constants'; // needs to be changed

export const MappingEditDrawer = (props: MappingEditDrawerProps) => {
  const { isOpen, onClose, configData, configItem, kiosk } = props;

  const [updateKioskHardware, resetKioskHardwareError] = useActions([
    kioskHardwareActions.updateKioskHardware,
    kioskHardwareActions.resetError
  ]);

  const { error: kioskHardwareError, pending: kioskHardwarePending } = useSelector(kioskHardwareSelectors.selectAction);

  const [error, setError] = useState<string>('');
  const [apiLoading, setAPILoading] = useState(false);
  const [uiLoading, setUILoading] = useState(false);

  // SETS THE FORM VALUES AND VALIDATION VALUES FOR EACH FORM VALUE (INVALID | VALID)
  const initialForm = getKioskHardwareInitialForm(configData);
  const [form, setForm] = useState(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});

  const classes = useStyles({ showDrawer: isOpen })();

  useEffect(() => {
    if (kioskHardwareError) {
      setError(kioskHardwareError as string);
      setUILoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskHardwareError]);

  useEffect(() => {
    setAPILoading(kioskHardwarePending as boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskHardwarePending]);

  useEffect(() => {
    if (uiLoading && !apiLoading && !error) {
      handleOverlayClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, apiLoading]);

  const onConfirm = () => {
    resetError();
    setUILoading(true);

    const payload: Partial<KioskHardware> = {
      id: configData.id,
      kioskId: form.kioskId,
      hardwareId: form.hardwareId,
      serialCode: form.serialCode ? form.serialCode : '',
      configuration: form.configuration ? JSON.parse(form.configuration as string) : {},
      values: form.values ? JSON.parse(form.values as string) : {},
      status: form.status
    };
    updateKioskHardware(payload);
  };

  const handleOverlayClose = () => {
    resetError();
    onClose();
  };

  const resetError = () => {
    if (error) {
      setError('');
      if (kioskHardwareError) {
        resetKioskHardwareError();
      }
    }
  };

  const onInputChange = (
    key: keyof ReturnType<typeof getKioskHardwareInitialForm>,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    resetError();
    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }

    setForm({ ...form, [key]: e.target.value });
  };

  return (
    <>
      {apiLoading && (
        <Backdrop open={apiLoading ? true : false}>
          <DotProgress color={color.GRAY} />
        </Backdrop>
      )}
      <Drawer open={isOpen} onClose={handleOverlayClose} hasBorderRadius>
        <Grid container className={classes.configEditOverlay}>
          <Card fullHeight>
            <Grid className={classes.configOverlayHeaderBlock}>
              <Grid className={classes.configOverlayHeading}>
                <FlexBox>
                  <Typography className={classes.configEditHeading} sx={{ fontWeight: 600 }}>
                    {kiosk.name}
                  </Typography>
                </FlexBox>
              </Grid>
              <Grid className={classes.configOverlayCloseIcon}>
                <SvgIconButton appearance="transparent" onClick={handleOverlayClose} data-testid="kiosk-restock-x">
                  <SvgIcon name="exit_circle" size={40} />
                </SvgIconButton>
              </Grid>
            </Grid>
            <Box padding="2 6" fullWidth bg="light-gray">
              <Typography fontWeight={600} fontSize={16}>
                Kiosk Hardware
              </Typography>
            </Box>
            <MuiBox className={classes.configContentBox}>
              <FlexBox fullWidth direction="column" margin="6 0">
                <Typography fontSize={16} fontWeight={600} sx={{ padding: '0 0 0 24px' }}>
                  Hardware
                </Typography>
                <Box width={34} />
                <Typography fontSize={16} sx={{ padding: '0 0 0 24px' }}>
                  {`${configItem.name} (${configItem.type})`}
                </Typography>
              </FlexBox>
              <MuiBox className={classes.createClientName}>
                <InputField
                  label="Serial Code"
                  size="medium"
                  fullWidth
                  placeholder="Serial Code"
                  value={form.serialCode ? form.serialCode : ''}
                  onChange={(e) => onInputChange('serialCode', e)}
                  error={validation.serialCode === INVALID}
                />
              </MuiBox>
              <MuiBox className={classes.createConfigInput}>
                <InputField
                  multiline
                  rows={8}
                  label="Configuration"
                  size="medium"
                  placeholder="Configuration"
                  value={form.configuration ? form.configuration : ''}
                  onChange={(e) => onInputChange('configuration', e)}
                  sx={{ width: '100%', height: '216px', borderRadius: '25px' }}
                  error={validation.configuration === INVALID}
                />
              </MuiBox>
              <MuiBox className={classes.createConfigInput}>
                <InputField
                  multiline
                  rows={8}
                  label="Values"
                  size="medium"
                  fullWidth
                  sx={{ width: '100%', height: '216px', borderRadius: '25px' }}
                  placeholder="Values"
                  value={form.values ? form.values : ''}
                  onChange={(e) => onInputChange('values', e)}
                  error={validation.values === INVALID}
                />
              </MuiBox>
              <Box margin="1 0 0">
                <Typography color={color.RED} fontSize={14}>
                  {error}
                </Typography>
              </Box>
              {/* button */}
              <MuiBox className={classes.saveButtonBox}>
                <Button customSize="small" appearance="primary" onClick={onConfirm} className={classes.saveButton}>
                  Save
                </Button>
              </MuiBox>
            </MuiBox>
          </Card>
        </Grid>
      </Drawer>
    </>
  );
};
